import { FileText, Mail } from "lucide-react";
import { useMemo, useState } from "react";
import { apiRequest } from "../lib/api";
import type { Wallet } from "../types";
import { ButtonLabel } from "./ButtonLabel";

const monthName = new Intl.DateTimeFormat("es-MX", { month: "long", year: "numeric", timeZone: "UTC" });

/**
 * Closed months Kuspit can still send: at most 12 back (Kuspit error 225) and not before the wallet
 * existed. The current month is excluded — its statement is generated after the monthly close.
 */
export function statementMonths(since: string, now: Date = new Date()): Array<{ year: number; month: number; label: string }> {
  // Kuspit's calendar is Mexico City's (UTC−6), as on the server.
  const local = (instant: Date) => new Date(instant.getTime() - 6 * 3_600_000);
  const sinceDate = local(new Date(since));
  const nowLocal = local(now);
  const floor = sinceDate.getUTCFullYear() * 12 + sinceDate.getUTCMonth();
  const current = nowLocal.getUTCFullYear() * 12 + nowLocal.getUTCMonth();
  const months: Array<{ year: number; month: number; label: string }> = [];
  for (let back = 1; back <= 12; back += 1) {
    const index = current - back;
    if (index < floor) break;
    const year = Math.floor(index / 12);
    const month = (index % 12) + 1;
    const label = monthName.format(new Date(Date.UTC(year, month - 1, 1)));
    months.push({ year, month, label: label.charAt(0).toUpperCase() + label.slice(1) });
  }
  return months;
}

/**
 * Contract and statements, delivered the only way Kuspit offers: it emails the PDF to the address
 * registered on the Kuspit contract and returns nothing to us (ticket G, 2026-09-22). The annual tax
 * certificate is not available through the API at all, so the card says where to find it.
 */
export function WalletDocumentsCard({ wallet }: { wallet: Wallet }) {
  const months = useMemo(() => statementMonths(wallet.createdAt), [wallet.createdAt]);
  const [period, setPeriod] = useState(() => (months[0] ? `${months[0].year}-${months[0].month}` : ""));
  const [busy, setBusy] = useState<"contract" | "statement" | "clabe" | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (!wallet.providerAccountRef) return null;

  const send = async (kind: "contract" | "statement" | "clabe") => {
    setBusy(kind); setNotice(null); setError(null);
    try {
      const [year, month] = period.split("-").map(Number);
      const response = await apiRequest<{ data: { delivery: "emailed" | "manual" } }>(`/api/v1/wallets/${wallet.id}/documents`, {
        method: "POST",
        body: JSON.stringify(kind === "statement" ? { kind, year, month } : { kind })
      });
      const what = kind === "contract" ? "Tu contrato" : kind === "clabe" ? "Tu constancia de cuenta CLABE" : "Tu estado de cuenta";
      setNotice(response.data.delivery === "manual"
        ? `${what} es muy pesado para el envío automático; el equipo de Kuspit te lo enviará por correo.`
        : `${what} va en camino al correo registrado en tu contrato de Kuspit. Puede tardar unos minutos.`);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No pudimos solicitar el documento.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="card documents-card" aria-labelledby="documents-title">
      <span className="rail-title" id="documents-title">Documentos</span>
      <p>Kuspit envía tu contrato, tu constancia CLABE y tus estados de cuenta en PDF al correo registrado en tu contrato.</p>
      <button type="button" className="secondary-button small" onClick={() => void send("contract")} disabled={busy !== null}>
        <ButtonLabel busy={busy === "contract"} busyLabel="Solicitando" label="Enviarme mi contrato" icon={<FileText size={14} aria-hidden="true" />} iconFirst />
      </button>
      <button type="button" className="secondary-button small" onClick={() => void send("clabe")} disabled={busy !== null}>
        <ButtonLabel busy={busy === "clabe"} busyLabel="Solicitando" label="Enviarme mi constancia CLABE" icon={<FileText size={14} aria-hidden="true" />} iconFirst />
      </button>
      {months.length > 0 ? (
        <div className="documents-statement">
          <label htmlFor="statement-period" className="sr-only">Mes del estado de cuenta</label>
          <select id="statement-period" className="standalone-input" value={period} onChange={(event) => setPeriod(event.target.value)} disabled={busy !== null}>
            {months.map((option) => <option key={`${option.year}-${option.month}`} value={`${option.year}-${option.month}`}>{option.label}</option>)}
          </select>
          <button type="button" className="secondary-button small" onClick={() => void send("statement")} disabled={busy !== null || !period}>
            <ButtonLabel busy={busy === "statement"} busyLabel="Solicitando" label="Enviar estado de cuenta" icon={<Mail size={14} aria-hidden="true" />} iconFirst />
          </button>
        </div>
      ) : (
        <p className="rail-empty">Tu primer estado de cuenta estará disponible después del cierre de este mes.</p>
      )}
      {notice && <div className="form-message" role="status">{notice}</div>}
      {error && <div className="form-error" role="alert">{error}</div>}
      <p className="documents-note">
        <strong>Constancia fiscal.</strong> Kuspit emite cada año, entre febrero y marzo, tu constancia de retenciones (CFDI) del año anterior.
        La recibes por correo de Kuspit o la descargas en su portal; Aventuria no puede obtenerla por ti.
      </p>
    </section>
  );
}
